import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useSession } from '../session';

/**
 * Register a company — the other half of §2.1.
 *
 * The login screen cannot detect "you have no company yet", because /login
 * answers an identical 401 whether the password is wrong or no account exists,
 * and that is what stops the form being used to enumerate customers. So this
 * route is OFFERED from there rather than redirected to.
 *
 * Creating an organisation makes the person its Super Admin. Everything about
 * the trial comes from the server (TRIAL_DAYS); nothing here invents a number.
 */

// Mirrors utils/password.js so the person is told before they submit rather than
// after. The server remains authoritative — this only saves a round trip.
const COMMON = new Set([
  'password', 'password1', 'password123', '12345678', '123456789', '1234567890',
  'qwerty123', 'qwertyuiop', 'iloveyou', 'admin123', 'welcome1', 'welcome123',
  'abc12345', 'letmein1', 'bhoomitrack', 'construction',
]);

function passwordProblem(pw: string, name: string, email: string): string | null {
  if (pw.length < 8) return 'At least 8 characters.';
  if (pw.length > 128) return 'At most 128 characters.';
  if (/^\d+$/.test(pw)) return 'Cannot be only numbers.';
  if (/^(.)\1+$/.test(pw)) return 'Cannot be one repeated character.';
  if (COMMON.has(pw.toLowerCase())) return 'That password is too common.';
  const local = email.split('@')[0];
  for (const personal of [name, local]) {
    const p = personal.trim().toLowerCase();
    if (p.length >= 4 && pw.toLowerCase().includes(p)) {
      return 'Must not contain your name or email.';
    }
  }
  return null;
}

export default function Register() {
  const { register } = useSession();
  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pwProblem = password ? passwordProblem(password, name, email) : null;
  const ready = companyName.trim() && name.trim() && email.trim() && password && !pwProblem;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!ready) return;
    setError(null);
    setBusy(true);
    try {
      await register({
        companyName: companyName.trim(), name: name.trim(),
        email: email.trim().toLowerCase(), password,
      });
      // No navigation needed: the session now has a user, so the app swaps to
      // the console on the next render.
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form className="login__card card" onSubmit={onSubmit} noValidate>
        <div className="login__brand">
          <span className="login__mark" aria-hidden="true" />
          <span className="login__name">BhoomiTrack</span>
        </div>
        <h1 className="login__title">Register your company</h1>
        <p className="muted login__sub">
          You become the Super Admin. You can invite owners and managers afterwards.
        </p>

        <label className="label" htmlFor="company">Company name</label>
        <input id="company" className="field" value={companyName} autoComplete="organization"
          onChange={(e) => setCompanyName(e.target.value)} placeholder="Ranjita Constructions" required />

        <label className="label" htmlFor="name" style={{ marginTop: 'var(--s-base)' }}>Your name</label>
        <input id="name" className="field" value={name} autoComplete="name"
          onChange={(e) => setName(e.target.value)} required />

        <label className="label" htmlFor="email" style={{ marginTop: 'var(--s-base)' }}>Email</label>
        <input id="email" className="field" type="email" value={email} autoComplete="username"
          onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />

        <label className="label" htmlFor="password" style={{ marginTop: 'var(--s-base)' }}>Password</label>
        <input id="password" className="field" type="password" value={password} autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
          aria-describedby="pw-help" aria-invalid={Boolean(touched && pwProblem)} required />
        <p id="pw-help" className={`login__hint ${touched && pwProblem ? 'login__hint--bad' : 'muted'}`}>
          {pwProblem ?? 'At least 8 characters. Not your name or email.'}
        </p>

        {error && (
          <div className="login__error" role="alert">
            <strong>{error}</strong>
            {/already exists/i.test(error) && (
              <span className="muted">
                <Link className="login__link" to="/login">Sign in instead</Link>, or use a different address.
              </span>
            )}
          </div>
        )}

        <button className="btn btn--gold login__submit" type="submit" disabled={busy || !ready}>
          {busy ? 'Creating your company…' : 'Create company'}
        </button>

        <p className="login__foot muted">
          Already have an account? <Link className="login__link" to="/login">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
